/**
 * Certificate Template Editor
 *
 * Admin component for configuring certificate templates. Two-pane layout:
 * preview on the left, compact configuration on the right. Only one
 * field's controls are visible at a time — switch via the chip selector
 * or by clicking a field in the preview.
 *
 * This is the orchestrator: it owns all template state, the load/save/
 * upload flows, and the geometry (scale) plumbing, and composes the
 * presentational panels under ./certificateTemplate/.
 */

import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CertificateTemplate, CertificateTextField, Course } from '../../apis/api-client';
import { certificateTemplatesClient } from '../../apis/client-instances';
import { Box, Button, CircularProgress } from '../ui';
import { VisibilityIcon } from '../icons';
import { alert, getFileUrl, uploadFile } from '../../hooks/function';
import { getCEInfo } from '../../utils/courseCEInfo';
import {
  DEFAULT_FIELDS,
  isCustomText,
  newCustomTextId,
  SYSTEM_FIELD_TYPES,
  TEMPLATE_HEIGHT,
  TEMPLATE_WIDTH,
} from './certificateTemplate/constants';
import CertificateBackgroundPanel from './certificateTemplate/CertificateBackgroundPanel';
import CertificateFieldChips from './certificateTemplate/CertificateFieldChips';
import CertificateFieldControls from './certificateTemplate/CertificateFieldControls';
import CertificatePreviewCanvas from './certificateTemplate/CertificatePreviewCanvas';
import CertificatePreviewModal from './certificateTemplate/CertificatePreviewModal';

export interface CertificateTemplateEditorProps {
  /**
   * Optional course context — when the editor is mounted from a course's
   * Completion tab, the preview substitutes real values for Course ID
   * and CE Hours so admins can verify positioning against actual data.
   * Server-side rendering still does the real substitution at issue time;
   * this just makes the preview informative.
   */
  course?: Course | null;
}

export default function CertificateTemplateEditor({ course }: CertificateTemplateEditorProps = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const observerRef = useRef<ResizeObserver | null>(null);
  const templateWidthRef = useRef(TEMPLATE_WIDTH);
  // File input bound to the "Replace background image" button — admins
  // click the button, the input opens the OS file picker.
  const backgroundInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState<CertificateTemplate>({
    name: 'Default',
    backgroundImage: null,
    width: TEMPLATE_WIDTH,
    height: TEMPLATE_HEIGHT,
    textFields: DEFAULT_FIELDS,
    isActive: true,
  });
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [scale, setScale] = useState(0);
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(SYSTEM_FIELD_TYPES.map((t) => [t, true]))
  );
  // Which field's config panel is visible. Single-selection — keeps the
  // right column to ~one screen height instead of three stacked blocks.
  const [selectedField, setSelectedField] = useState<string>('StudentName');
  // Live-preview modal — opens a clean render of the certificate at the
  // dialog's max size with no drag handles or edit chrome, so admins
  // can see exactly what learners will receive.
  const [previewOpen, setPreviewOpen] = useState(false);
  // Width of the preview modal's inner content — feeds the dialog's
  // scale calculation. Updated by a ResizeObserver on the modal container.
  const [previewWidth, setPreviewWidth] = useState(0);
  const previewModalRef = React.useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      setPreviewWidth(0);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setPreviewWidth(w);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Resolves the text shown in the live preview for any given field.
  // System fields use the course context (when available) so admins see
  // real values instead of bracketed placeholders. Custom-text fields
  // render their `content`. Server-side rendering still substitutes for
  // real at issue time; this is preview-only.
  const liveValueForField = useMemo(() => {
    const courseSlug = (course as any)?.slug as string | undefined;
    const courseTitle = course?.title;
    const code = course?.courseCode ?? '';
    // AGD Subject Code — read from CE Information then strip everything
    // except digits. Admins often type values like "AGD 9999" or "9999 —
    // Implants" in the CE Info field, but on the certificate the AGD
    // wants just the raw 4-digit code. The regex pulls the first
    // contiguous run of digits and discards the rest; if no digits are
    // present, falls back to the original string so weirdly-formatted
    // values still surface something (rather than a silent empty).
    let agdValue = '';
    if (courseSlug) {
      const raw = (getCEInfo(course?.ceInfo).agdSubjectCode || '').trim();
      if (raw) {
        const digits = raw.match(/\d+/);
        agdValue = digits ? digits[0] : raw;
      }
    }
    // CE hours read straight off the course scalar. Singular/plural so
    // "1 CE Hour" reads correctly.
    const ceHoursValue =
      course?.ceHours != null
        ? `${course.ceHours} CE Hour${course.ceHours === 1 ? '' : 's'}`
        : '';
    return (field: CertificateTextField): string => {
      if (isCustomText(field.fieldType)) return field.content || '[Custom text]';
      switch (field.fieldType) {
        case 'StudentName':
          return '[Student Name]';
        case 'CourseTitle':
          return courseTitle || '[Course Title]';
        case 'CourseId':
          return code || '[Course ID]';
        case 'CeHours':
          return ceHoursValue || '[CE Hours]';
        case 'AgdSubjectCode':
          return agdValue || '[AGD Subject Code]';
        case 'CompletionDate':
          return new Date().toLocaleDateString();
        default:
          return `[${field.fieldType}]`;
      }
    };
  }, [course]);

  // Keep width ref in sync
  templateWidthRef.current = template.width;

  // Callback ref: attaches ResizeObserver when the preview element mounts
  const previewRef = React.useCallback((node: HTMLDivElement | null) => {
    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setScale(width / templateWidthRef.current);
        }
      }
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  // Load existing template
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        // 404 = no active template yet (normal first-run); treat as null
        // so the editor opens with defaults instead of erroring.
        let existing: CertificateTemplate | null = null;
        try {
          existing = await certificateTemplatesClient.getActiveTemplate();
        } catch {
          /* no active template */
        }
        if (existing) {
          setTemplate(existing);
          templateWidthRef.current = existing.width || TEMPLATE_WIDTH;

          // Determine which fields are enabled — start all off, flip on
          // for any field type present in the saved template.
          const enabled: Record<string, boolean> = Object.fromEntries(SYSTEM_FIELD_TYPES.map((t) => [t, false]));
          existing.textFields.forEach((f) => {
            enabled[f.fieldType] = true;
          });
          setEnabledFields(enabled);

          // Load the tenant's own uploaded background, if any. No built-in
          // default — when nothing is uploaded the canvas stays blank (white).
          if (existing.backgroundImage) {
            const url = await getFileUrl(existing.backgroundImage);
            setBackgroundUrl(url || null);
          } else {
            setBackgroundUrl(null);
          }

        } else {
          // No template yet. There is no default artwork to seed and we do
          // NOT auto-create a template — the editor opens on a blank canvas
          // with the standard field layout (from the initial state above),
          // and the row is created only when the admin clicks Save. Until
          // then the server has no active template and simply issues no
          // certificate, which is the intended "unconfigured" behavior.
          setBackgroundUrl(null);
        }
      } catch (err) {
        console.error('Failed to load template:', err);
        setBackgroundUrl(null);
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, []);

  // Handle background image upload
  const handleBackgroundUpload = async (file: File | null) => {
    if (!file) {
      // Clearing the upload removes the background and persists that in the
      // same click. Templates are tenant-scoped (one row per account) — without
      // the auto-save the admin would see a blank canvas while the server-side
      // renderer kept serving the stale custom upload. There's no default to
      // revert to, so the certificate simply renders on a white background.
      setBackgroundUrl(null);
      const next = { ...template, backgroundImage: null };
      try {
        const saved = await certificateTemplatesClient.saveTemplate(next);
        setTemplate(saved);
        alert(enqueueSnackbar, 'Background image removed', 'success');
      } catch {
        // Save failed — drop the in-flight UI to the cleared local
        // state anyway so the admin can retry from the same context.
        setTemplate(next);
        alert(enqueueSnackbar, 'Failed to remove background — try again', 'error');
      }
      return;
    }

    try {
      // Read actual image dimensions before uploading
      const imgDimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });

      const result = await uploadFile(file);
      if (result?.success && result.uploadedUrl) {
        // Use actual image dimensions as the template coordinate space.
        // Rescale existing field positions from old dimensions to new.
        const scaleX = imgDimensions.width / templateWidthRef.current;
        const scaleY = imgDimensions.height / (template.height || TEMPLATE_HEIGHT);
        templateWidthRef.current = imgDimensions.width;
        setTemplate((prev) => ({
          ...prev,
          backgroundImage: result.uploadedUrl,
          width: imgDimensions.width,
          height: imgDimensions.height,
          textFields: prev.textFields.map((f) => ({
            ...f,
            x: Math.round(f.x * scaleX),
            y: Math.round(f.y * scaleY),
            fontSize: Math.round(f.fontSize * Math.min(scaleX, scaleY)),
          })),
        }));
        const url = await getFileUrl(result.uploadedUrl);
        setBackgroundUrl(url || null);
        alert(enqueueSnackbar, 'Background image uploaded', 'success');
      }
    } catch (err) {
      alert(enqueueSnackbar, 'Failed to upload background image', 'error');
    }
  };

  // Update a field's property
  const updateField = (fieldType: string, updates: Partial<CertificateTextField>) => {
    setTemplate((prev) => ({
      ...prev,
      textFields: prev.textFields.map((f) => (f.fieldType === fieldType ? { ...f, ...updates } : f)),
    }));
  };

  // Toggle field on/off
  const toggleField = (fieldType: string, enabled: boolean) => {
    setEnabledFields((prev) => ({ ...prev, [fieldType]: enabled }));
    if (enabled) {
      const exists = template.textFields.some((f) => f.fieldType === fieldType);
      if (!exists) {
        const defaultField = DEFAULT_FIELDS.find((f) => f.fieldType === fieldType)!;
        setTemplate((prev) => ({
          ...prev,
          textFields: [...prev.textFields, defaultField],
        }));
      }
    } else {
      setTemplate((prev) => ({
        ...prev,
        textFields: prev.textFields.filter((f) => f.fieldType !== fieldType),
      }));
    }
  };

  // Handle drag end — update position
  const handleDragStop = (fieldType: string, x: number, y: number) => {
    const templateX = Math.round(x / scale);
    const templateY = Math.round(y / scale);
    updateField(fieldType, { x: templateX, y: templateY });
  };

  // Append a new custom-text field at a centered default position and
  // auto-select it so the admin can immediately edit the content.
  const handleAddCustomText = () => {
    const id = newCustomTextId();
    const newField: CertificateTextField = {
      fieldType: id,
      x: Math.round(template.width / 2),
      y: Math.round(template.height / 2),
      fontSize: 32,
      fontFamily: 'Arial',
      fontColor: '#000000',
      fontWeight: 'normal',
      textAlign: 'center',
      content: 'New text',
    };
    setTemplate((prev) => ({ ...prev, textFields: [...prev.textFields, newField] }));
    setSelectedField(id);
  };

  const handleDeleteCustomText = (fieldType: string) => {
    setTemplate((prev) => ({
      ...prev,
      textFields: prev.textFields.filter((f) => f.fieldType !== fieldType),
    }));
    // If the deleted field was selected, fall back to a system field.
    setSelectedField((cur) => (cur === fieldType ? 'StudentName' : cur));
  };

  // Save template
  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await certificateTemplatesClient.saveTemplate(template);
      // The backend's handling of `CustomText:<uuid>` fields is flaky:
      // sometimes the `content` field comes back empty, sometimes the
      // whole row is dropped from the echoed template. In either case
      // we want the editor to keep what the admin just typed, so we
      // reconcile the saved echo against the local in-flight state:
      //   • For every saved custom-text row with empty content, restore
      //     content from the matching local row (matched by fieldType,
      //     which is the per-row uuid).
      //   • For every local custom-text row the server dropped from
      //     the echo entirely, re-append it as-is.
      // System fields (StudentName etc.) always come back intact, so
      // they pass through untouched.
      const savedFieldTypes = new Set(saved.textFields.map((f) => f.fieldType));
      const localCustomFields = template.textFields.filter((f) =>
        isCustomText(f.fieldType),
      );
      const restoredFields = saved.textFields.map((sf) => {
        if (!isCustomText(sf.fieldType)) return sf;
        if (sf.content) return sf;
        const localMatch = localCustomFields.find(
          (lf) => lf.fieldType === sf.fieldType,
        );
        return localMatch?.content
          ? { ...sf, content: localMatch.content }
          : sf;
      });
      const droppedCustomFields = localCustomFields.filter(
        (lf) => !savedFieldTypes.has(lf.fieldType),
      );
      setTemplate({
        ...saved,
        textFields: [...restoredFields, ...droppedCustomFields],
      });
      alert(enqueueSnackbar, 'Certificate template saved', 'success');
    } catch (err) {
      alert(enqueueSnackbar, 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left: Live Preview */}
        <CertificatePreviewCanvas
          template={template}
          scale={scale}
          backgroundUrl={backgroundUrl}
          selectedField={selectedField}
          previewRef={previewRef}
          liveValueForField={liveValueForField}
          onSelectField={setSelectedField}
          onDragStop={handleDragStop}
        />

        {/* Right: Configuration Panel — compact, single-field at a time */}
        <Box sx={{ flex: 3, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CertificateBackgroundPanel
            hasCustomBackground={!!template.backgroundImage}
            backgroundInputRef={backgroundInputRef}
            onUpload={handleBackgroundUpload}
          />

          <CertificateFieldChips
            template={template}
            selectedField={selectedField}
            enabledFields={enabledFields}
            onSelectField={setSelectedField}
            onToggleField={toggleField}
            onAddCustomText={handleAddCustomText}
            onDeleteCustomText={handleDeleteCustomText}
          />

          <CertificateFieldControls
            template={template}
            selectedField={selectedField}
            enabledFields={enabledFields}
            onUpdateField={updateField}
          />

          {/* ── Preview + Save (sticky-feel at bottom of column) ──────── */}
          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
              onClick={() => setPreviewOpen(true)}
              sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
            >
              Preview certificate
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSave}
              disabled={saving}
              sx={{ height: 44 }}
            >
              {saving ? <CircularProgress size={22} /> : 'Save Template'}
            </Button>
          </Box>
        </Box>
      </Box>

      <CertificatePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        template={template}
        backgroundUrl={backgroundUrl}
        liveValueForField={liveValueForField}
        previewModalRef={previewModalRef}
        previewWidth={previewWidth}
      />
    </Box>
  );
}
